import { AxiosError } from 'axios';
import i18n from '@/i18n';
import { edit, generateQueryString, get, post } from '@/utils';
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';

import { IFspLight } from '../use-project-types/types';
import {
	CreateInvitationPayload,
	IInvitationAdministration,
	IInvitationAdministrationLevel,
	IInvitationStatusInfo,
	IInvitationsQuery,
	Invitatie,
	InvitatieEvent,
	InvitatieStatus,
	normalizeInvitationStatus
} from './types';

type RawInvitationStatus = {
	id?: number;
	name?: string;
};

type RawInvitationLevel = {
	id?: number;
	name?: string;
	description?: string;
};

type RawInvitation = {
	id?: number;
	email?: string;
	roleId?: number;
	roleName?: string;
	/** Noul contract: rolul vine imbricat ({ id, name, label }). */
	role?: {
		id?: number;
		name?: string;
		label?: string;
	};
	administrations?: Array<{
		id?: number;
		name?: string;
		code?: string;
		level?: RawInvitationLevel;
	}>;
	status?: {
		id?: number;
		name?: string;
	};
	expiresAt?: string;
	counter?: number;
	createdBy?: {
		id?: number;
		firstName?: string;
		lastName?: string;
	};
	acceptedUser?: {
		id?: number;
		firstName?: string;
		lastName?: string;
	};
	createdAt?: string;
	updatedAt?: string;
};

type RawInvitationLog = {
	id?: number;
	status?: string;
	performedBy?: {
		id?: number;
		firstName?: string;
		lastName?: string;
	};
	performedAt?: string;
};

type InvitationsResponse = {
	items?: RawInvitation[];
	pageNumber?: number;
	totalPages?: number;
	totalCount?: number;
	orderBy?: string | null;
	ascending?: boolean;
	hasPreviousPage?: boolean;
	hasNextPage?: boolean;
};

type InvitationLogsResponse = {
	items?: RawInvitationLog[];
	pageNumber?: number;
	totalPages?: number;
	totalCount?: number;
	orderBy?: string | null;
	ascending?: boolean;
	hasPreviousPage?: boolean;
	hasNextPage?: boolean;
};

// Statusurile invitațiilor (api/invitation-statuses); folosite ca fallback dacă endpoint-ul e gol.
const FALLBACK_STATUSES: IInvitationStatusInfo[] = [
	{ id: 1, name: 'Pending', uiStatus: 'trimisa' },
	{ id: 2, name: 'Accepted', uiStatus: 'acceptata' },
	{ id: 3, name: 'Rejected', uiStatus: 'refuzata' },
	{ id: 4, name: 'Expired', uiStatus: 'expirata' },
	{ id: 5, name: 'Revoked', uiStatus: 'revocata' }
];

const normalizeAdministrationLevel = (
	rawLevel?: RawInvitationLevel
): IInvitationAdministrationLevel | undefined => {
	if (!rawLevel) return undefined;

	return {
		id: Number(rawLevel.id || 0),
		name: String(rawLevel.name || ''),
		description: rawLevel.description ? String(rawLevel.description) : undefined
	};
};

const normalizeAdministration = (
	rawAdministration: NonNullable<RawInvitation['administrations']>[number]
): IInvitationAdministration => ({
	id: Number(rawAdministration.id || 0),
	name: String(rawAdministration.name || ''),
	code: rawAdministration.code ? String(rawAdministration.code) : undefined,
	level: normalizeAdministrationLevel(rawAdministration.level)
});

const normalizeInvitationLogEvent = (rawLog: RawInvitationLog): InvitatieEvent => {
	const status = normalizeInvitationStatus(rawLog.status);
	const byName =
		`${rawLog.performedBy?.firstName || ''} ${rawLog.performedBy?.lastName || ''}`.trim() ||
		'Sistem';

	return {
		id: `log-${rawLog.id || rawLog.performedAt || byName}-${status}`,
		tip: status,
		at: String(rawLog.performedAt || ''),
		byName
	};
};

const buildInitialHistory = (
	status: InvitatieStatus,
	invitedByName: string,
	createdAt: string,
	updatedAt?: string
): InvitatieEvent[] => {
	const history: InvitatieEvent[] = [
		{
			id: `evt-created-${createdAt}-${invitedByName}`,
			tip: 'trimisa',
			at: createdAt,
			byName: invitedByName
		}
	];

	if (status !== 'trimisa' && updatedAt) {
		history.push({
			id: `evt-status-${updatedAt}-${status}`,
			tip: status,
			at: updatedAt,
			byName: invitedByName
		});
	}

	return history;
};

const normalizeInvitation = (rawInvitation: RawInvitation): Invitatie => {
	const administrations = Array.isArray(rawInvitation.administrations)
		? rawInvitation.administrations.map(normalizeAdministration)
		: [];
	const administrationName =
		administrations.length > 0
			? administrations.map((administration) => administration.name).join(', ')
			: null;
	const invitedByName =
		`${rawInvitation.createdBy?.firstName || ''} ${rawInvitation.createdBy?.lastName || ''}`.trim() ||
		'Sistem';
	// Numele utilizatorului care a acceptat invitația (dacă există).
	const acceptedUserName =
		`${rawInvitation.acceptedUser?.firstName || ''} ${rawInvitation.acceptedUser?.lastName || ''}`.trim() ||
		undefined;
	const statusName = String(rawInvitation.status?.name || '');
	const status = normalizeInvitationStatus(statusName);
	const createdAt = String(rawInvitation.createdAt || '');
	const updatedAt = rawInvitation.updatedAt ? String(rawInvitation.updatedAt) : undefined;

	return {
		id: Number(rawInvitation.id || 0),
		email: String(rawInvitation.email || ''),
		nume: acceptedUserName,
		roleId:
			rawInvitation.role?.id ??
			(rawInvitation.roleId === undefined || rawInvitation.roleId === null
				? undefined
				: Number(rawInvitation.roleId)),
		// afișare: preferăm eticheta localizată (label), fallback pe name / roleName
		roleName: String(
			rawInvitation.role?.label ||
				rawInvitation.role?.name ||
				rawInvitation.roleName ||
				''
		),
		administrations,
		administrationName,
		acceptedUserId:
			rawInvitation.acceptedUser?.id === undefined ||
			rawInvitation.acceptedUser?.id === null
				? undefined
				: Number(rawInvitation.acceptedUser.id),
		acceptedUserName,
		statusId:
			rawInvitation.status?.id === undefined || rawInvitation.status?.id === null
				? undefined
				: Number(rawInvitation.status.id),
		statusName: statusName || undefined,
		status,
		invitedByName,
		createdById:
			rawInvitation.createdBy?.id === undefined || rawInvitation.createdBy?.id === null
				? undefined
				: Number(rawInvitation.createdBy.id),
		sentAt: createdAt,
		expiresAt: String(rawInvitation.expiresAt || ''),
		updatedAt,
		nrTrimiteri: Number(rawInvitation.counter || 0),
		istoric: buildInitialHistory(status, invitedByName, createdAt, updatedAt),
		token: undefined
	};
};

async function fetchSingleInvitationLogs(
	invitationId: number | string
): Promise<InvitatieEvent[] | null> {
	try {
		const queryString = generateQueryString({
			PageNumber: 1,
			PageSize: 100,
			Ascending: false
		});
		const res = await get(`api/invitations/${invitationId}/logs${queryString}`);
		const data = res.data as InvitationLogsResponse;
		const items = Array.isArray(data.items) ? data.items : [];

		return items
			.map(normalizeInvitationLogEvent)
			.filter((event) => Boolean(event.at))
			.sort((a, b) => b.at.localeCompare(a.at));
	} catch {
		return null;
	}
}

type RawAdministrationListItem = {
	id?: number;
	name?: string;
	avatar?: { publicUrl?: string } | string | null;
};

type AdministrationsListResponse = {
	items?: RawAdministrationListItem[];
};

// Lista administrațiilor pentru dropdown vine din endpoint-ul real, paginat.
async function fetchInvitationAdministrations(): Promise<IFspLight[]> {
	try {
		const res = await get('api/administrations?PageNumber=1&PageSize=500');
		const data = res.data as AdministrationsListResponse;
		const items = Array.isArray(data.items) ? data.items : [];

		return items.map((item) => ({
			id: Number(item.id || 0),
			name: String(item.name || ''),
			avatar:
				typeof item.avatar === 'string' ? item.avatar : (item.avatar?.publicUrl ?? '')
		}));
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error?: string; message?: string };
		throw new Error(
			data?.message || data?.error || i18n.t('invitations.errorLoadAdministrations')
		);
	}
}

async function fetchInvitationStatuses(): Promise<IInvitationStatusInfo[]> {
	try {
		const res = await get('api/invitation-statuses');
		const items = Array.isArray(res.data) ? (res.data as RawInvitationStatus[]) : [];
		const statuses = items.map((status) => ({
			id: Number(status.id || 0),
			name: String(status.name || ''),
			uiStatus: normalizeInvitationStatus(status.name)
		}));

		return statuses.length > 0
			? statuses
			: FALLBACK_STATUSES.map((status) => ({ ...status }));
	} catch {
		return FALLBACK_STATUSES.map((status) => ({ ...status }));
	}
}

async function fetchInvitations(
	query: IInvitationsQuery | undefined,
	page: number,
	items: number,
	onResponse?: (pageParam: number, itemsParam: number) => void
): Promise<Invitatie[]> {
	try {
		const queryString = generateQueryString({
			...query,
			PageNumber: page + 1,
			PageSize: items
		});
		const res = await get(`api/invitations${queryString}`);
		const data = res.data as InvitationsResponse;
		const normalized = (data.items || []).map(normalizeInvitation);

		onResponse?.(data.totalPages || 1, data.totalCount || 0);

		return normalized;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error?: string; message?: string };
		throw new Error(data?.message || data?.error || i18n.t('invitations.errorLoad'));
	}
}

async function fetchSingleInvitation(invitationId: number | string): Promise<Invitatie> {
	try {
		const invitationRes = await get(`api/invitations/${invitationId}`);
		return normalizeInvitation(invitationRes.data as RawInvitation);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error?: string; message?: string };
		throw new Error(data?.message || data?.error || i18n.t('invitations.errorNotFound'));
	}
}

async function postInvitation(payload: CreateInvitationPayload): Promise<Invitatie> {
	try {
		const res = await post<
			{
				email: string;
				roleId: number;
				administrationIds: number[];
				countyId?: number;
				uatId?: number;
			},
			RawInvitation
		>('api/invitations', {
			email: payload.email.trim(),
			roleId: payload.roleId,
			administrationIds: payload.administrationIds,
			// Județ + UAT doar pentru moderator (altfel `null` → le omitem, BE le vrea absente).
			...(payload.countyId != null ? { countyId: payload.countyId } : {}),
			...(payload.uatId != null ? { uatId: payload.uatId } : {})
		});
		return normalizeInvitation(res.data as RawInvitation);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error?: string; message?: string };
		throw new Error(data?.message || data?.error || i18n.t('invitations.errorSend'));
	}
}

// BE: POST api/invitations/{id}/resend → 200 OK (fără body); reîncărcăm invitația actualizată.
async function resendStoredInvitation(invitationId: number | string): Promise<Invitatie> {
	await post(`api/invitations/${invitationId}/resend`);
	return fetchSingleInvitation(invitationId);
}

// BE: PATCH api/invitations/{id}/cancel → 200 OK (fără body); reîncărcăm invitația actualizată.
async function revokeStoredInvitation(invitationId: number | string): Promise<Invitatie> {
	await edit(`api/invitations/${invitationId}/cancel`);
	return fetchSingleInvitation(invitationId);
}

// GET api/invitations/table-config — configurația coloanelor tabelului (chei, etichete,
// ordine, vizibilitate/sortabilitate implicite). Se corelează în pagină cu definițiile
// locale prin applyServerColumnConfig.
async function fetchInvitationsTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get('api/invitations/table-config');
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să încărcăm configurația tabelului.');
	}
}

export {
	fetchInvitationsTableConfig,
	fetchInvitationAdministrations,
	fetchInvitationStatuses,
	fetchInvitations,
	fetchSingleInvitationLogs,
	fetchSingleInvitation,
	postInvitation,
	resendStoredInvitation,
	revokeStoredInvitation
};
